import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  ChevronRight,
  Search,
  Sparkles,
  Waves,
  Coffee,
  Banknote,
  Package,
  Users,
} from "lucide-react";
import "./SpaRevenueDashboard.css";
import axios from 'axios';
import { getTodayMonth } from '../../utils/Date/SupportDate';
import RCSumDropDownSelectBranch from "../../components/RCSumTable/RCSumDropDownSelectBranch";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { reportManager, datePickup, sumTable, unitsAction, modalAction } from '../../redux/actions';

/**
 * SpaRevenueDashboard
 * ---------------------------------------------------------------------------
 * Drop-in report component for the spa service revenue payload.
 * Plain CSS (SpaRevenueDashboard.css) — no Tailwind required.
 *
 * Usage with data you already have:
 *   <SpaRevenueDashboard data={reportJson} />
 *
 * Usage with data fetched later (e.g. from your own API):
 *   <SpaRevenueDashboard dataUrl="/api/reports/2026-07-20" />
 *   The component fetches on mount and shows loading / error states.
 *
 * If neither prop is given, it renders the embedded sample payload below
 * (the report used to build this component) so the file works standalone.
 *
 * Expected shape of `data`:
 * {
 *   massage: {
 *     types: { "40": { count, revenue }, "70": {...}, "100": {...} },
 *     staffs: {
 *       [staffId]: { "40": n, "70": n, "100": n, totalTurns, items: [
 *         { invoice_id, version, service, price }
 *       ]}
 *     },
 *     voucherCount, totalQuantity, totalRevenue, items: [...]
 *   },
 *   rotenburo: { totalQuantity, totalRevenue, items: [...] },
 *   exchange:  { totalQuantity, totalRevenue, items: [...] },
 *   breakfast: { totalQuantity, totalRevenue, items: [...] },
 *   other:     { totalQuantity, totalRevenue, items: [...] }
 * }
 * ---------------------------------------------------------------------------
 */

const DURATIONS = ["40", "70", "100"];
const DURATION_COLOR = { "40": "#64748b", "70": "#d97706", "100": "#0d9488" };
const DURATION_BADGE = {
  "40": { background: "#f1f5f9", color: "#334155" },
  "70": { background: "#fef3c7", color: "#92400e" },
  "100": { background: "#ccfbf1", color: "#0f766e" },
};

const OTHER_CATEGORIES = [
  { key: "rotenburo", label: "Rotenburo", icon: Waves },
  { key: "exchange", label: "Currency exchange", icon: Banknote },
  { key: "breakfast", label: "Breakfast", icon: Coffee },
  { key: "other", label: "Other", icon: Package },
];

function formatCurrency(n) {
  return Math.round(n || 0).toLocaleString("en-US") + " VND";
}

function formatNumber(n) {
  return Math.round(n || 0).toLocaleString("en-US");
}

// Pull the duration ("40" | "70" | "100") out of a free-text service label,
// e.g. "Rotenburo + Massage 70 min" -> "70".
function parseDuration(service) {
  if (!service) return null;
  for (const d of DURATIONS) {
    if (service.includes(`${d} min`)) return d;
  }
  return null;
}

// Build a per-duration breakdown (turns from the authoritative staff record,
// revenue + line-item count derived from the invoice items) for one staff member.
function buildStaffBreakdown(staffRecord) {
  const revenueByDuration = { "40": 0, "70": 0, "100": 0, other: 0 };
  const itemCountByDuration = { "40": 0, "70": 0, "100": 0, other: 0 };

  (staffRecord.items || []).forEach((item) => {
    const d = parseDuration(item.service) || "other";
    revenueByDuration[d] += item.price || 0;
    itemCountByDuration[d] += 1;
  });

  const totalRevenue = Object.values(revenueByDuration).reduce((a, b) => a + b, 0);

  return DURATIONS.map((d) => ({
    duration: d,
    turns: staffRecord[d] || 0,
    revenue: revenueByDuration[d],
    lineItems: itemCountByDuration[d],
  })).concat(
    itemCountByDuration.other
      ? [{ duration: "other", turns: null, revenue: revenueByDuration.other, lineItems: itemCountByDuration.other }]
      : []
  ).map((row) => ({ ...row, totalRevenue }));
}

function CategoryCard({ label, revenue, quantity, Icon, active }) {
  return (
    <div className={`srd-cat-card${active ? " active" : ""}`}>
      <div className="srd-cat-icon-row">
        <Icon size={14} />
        {label}
      </div>
      <div className="srd-cat-revenue">{formatCurrency(revenue)}</div>
      <div className="srd-cat-qty">{formatNumber(quantity)} transactions</div>
    </div>
  );
}

function StaffTypeBreakdown({ row }) {
  if (row.duration === "other") {
    return (
      <div className="srd-breakdown-box unclassified">
        <div className="srd-breakdown-sub" style={{ marginTop: 0, marginBottom: 4 }}>Unclassified</div>
        <div className="srd-breakdown-value">{formatCurrency(row.revenue)}</div>
        <div className="srd-breakdown-sub">
          {row.lineItems} line item{row.lineItems !== 1 ? "s" : ""}
        </div>
      </div>
    );
  }
  const share = row.totalRevenue ? Math.round((row.revenue / row.totalRevenue) * 100) : 0;
  return (
    <div className="srd-breakdown-box">
      <span className="srd-badge" style={DURATION_BADGE[row.duration]}>
        {row.duration} min
      </span>
      <div className="srd-breakdown-value">{formatCurrency(row.revenue)}</div>
      <div className="srd-breakdown-sub">
        {row.turns} turn{row.turns !== 1 ? "s" : ""} · {row.lineItems} invoice line{row.lineItems !== 1 ? "s" : ""}
      </div>
      <div className="srd-breakdown-track">
        <div className="srd-breakdown-fill" style={{ width: `${share}%`, backgroundColor: DURATION_COLOR[row.duration] }} />
      </div>
      <div className="srd-breakdown-share">{share}% of this staff's massage revenue</div>
    </div>
  );
}

function StaffRow({ staffId, record, isOpen, onToggle }) {
  const revenue = (record.items || []).reduce((sum, i) => sum + (i.price || 0), 0);
  const breakdown = useMemo(() => buildStaffBreakdown(record), [record]);
  const total = DURATIONS.reduce((sum, d) => sum + (record[d] || 0), 0);

  return (
    <>
      <tr onClick={onToggle} className="srd-staff-row">
        <td style={{ width: 24 }}>
          <span className={`srd-chevron${isOpen ? " open" : ""}`}>
            <ChevronRight size={15} />
          </span>
        </td>
        <td className="srd-staff-name">Staff {staffId}</td>
        <td>
          <div className="srd-mixbar">
            {DURATIONS.map((d) => (
              <div
                key={d}
                style={{
                  width: total ? `${((record[d] || 0) / total) * 100}%` : 0,
                  backgroundColor: DURATION_COLOR[d],
                }}
              />
            ))}
          </div>
        </td>
        <td className="srd-td-right srd-staff-turns">{record.totalTurns}</td>
        <td className="srd-td-right srd-staff-revenue">{formatCurrency(revenue)}</td>
      </tr>
      {isOpen && (
        <tr className="srd-expand-row">
          <td colSpan={5} className="srd-expand-cell">
            <div className="srd-breakdown-grid">
              {breakdown.map((row) => (
                <StaffTypeBreakdown key={row.duration} row={row} />
              ))}
            </div>
            <div className="srd-subhead">Invoice lines</div>
            <div className="srd-invoice-box">
              <table className="srd-invoice-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Service</th>
                    <th className="srd-th-right">Version</th>
                    <th className="srd-th-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {record.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.invoice_id}</td>
                      <td>{item.service}</td>
                      <td className="srd-td-right">v{item.version}</td>
                      <td className="srd-td-right">{formatCurrency(item.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SpaRevenueDashboard({ data, dataUrl }) {
  const [remoteData, setRemoteData] = useState(null);
  const [loading, setLoading] = useState(Boolean(dataUrl));
  const [error, setError] = useState(null);
  const [openStaff, setOpenStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("rotenburo");
  const [search, setSearch] = useState("");
  const [selectDateInput, setSelectDataInput] = useState();
  const [dataRender, setDataRender] = useState({
        "massage": {
          "types": {
            "40": { "count": 0, "revenue": 0 },
            "70": { "count": 0, "revenue": 0 },
            "100": { "count": 0, "revenue": 0 }
          },
          "staffs": {},
          "voucherCount": 0,
          "totalQuantity": 0,
          "totalRevenue": 0,
          "items": []
        },
        "breakfast": {
          "totalQuantity": 0,
          "totalRevenue": 0,
          "items": []
        },
        "rotenburo": {
          "totalQuantity": 0,
          "totalRevenue": 0,
          "items": []
        },
        "exchange": {
          "totalQuantity": 0,
          "totalRevenue": 0,
          "items": []
        },
        "other": {
          "totalQuantity": 0,
          "totalRevenue": 0,
          "items": []
        }
      })

  const auth = useSelector(state => state.auth);
  const token = useSelector(state => state.token);
  const allBranchInform = useSelector(state => state.allBranchInformation);

  const dataBranchOnState = useSelector(state => state.datepickup.serivceInvoiceStatic);
  const branchIdOnState = dataBranchOnState.ID;
  const dispatch = useDispatch();

  const { role, area } = auth.user;

  var [selectArea, setSelectArea] = useState("Select Area");
  var [selectBranch, setSelectBranch] = useState("Select Branch");
  // const useMakeSumBySup = (access['MAN SUM'] === "/manager_sum" && role === 3);

  // const { branchReport, monthReport } = useParams();
  const isMultiBranch = (role === 6 || role === 7 || role === 1);
  const branchID = isMultiBranch ? branchIdOnState : auth.user.branchID;

  const listArea = allBranchInform === undefined ? [] : Object.keys(allBranchInform);
  var listBranch = (role === 6) ? renderListBranch(area) : (role === 7) ? renderListBranch(selectArea) : [];

  useEffect(() => {
    if (!dataUrl) return;
   // setSelectDataInput(getTodayMonth())
    setLoading(true);
    setError(null);
    fetch(dataUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then((json) => {
        setRemoteData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Could not load report data.");
        setLoading(false);
      });
  }, [dataUrl]);

  function renderListBranch(areaValue) {
    let list = []
    if (JSON.stringify(allBranchInform) !== '{}' && areaValue !== "Select Area") {
        allBranchInform[areaValue].forEach(branch => {
            list.push(branch.name);
        })
    } else {
        list = []
    }
    return list
  }

  const report = data || remoteData || dataRender;

  if (loading) {
    return <div className="srd-state">Loading report...</div>;
  }
  if (error) {
    return <div className="srd-state error">Couldn't load the report. {error}</div>;
  }
  if (!report) {
    return <div className="srd-state">No data yet. Pass a `data` object or a `dataUrl` to load a report.</div>;
  }

  const categories = {
    massage: { label: "Massage", revenue: report.massage.totalRevenue, quantity: report.massage.totalQuantity, icon: Sparkles },
    rotenburo: { label: "Rotenburo", revenue: report.rotenburo.totalRevenue, quantity: report.rotenburo.totalQuantity, icon: Waves },
    exchange: { label: "Currency exchange", revenue: report.exchange.totalRevenue, quantity: report.exchange.totalQuantity, icon: Banknote },
    breakfast: { label: "Breakfast", revenue: report.breakfast.totalRevenue, quantity: report.breakfast.totalQuantity, icon: Coffee },
    other: { label: "Other", revenue: report.other.totalRevenue, quantity: report.other.totalQuantity, icon: Package },
  };
  const grandTotal = Object.values(categories).reduce((sum, c) => sum + c.revenue, 0);
  const grandQty = Object.values(categories).reduce((sum, c) => sum + c.quantity, 0);

  const typeChartData = DURATIONS.map((d) => ({
    duration: `${d} min`,
    revenue: report.massage.types[d]?.revenue || 0,
    count: report.massage.types[d]?.count || 0,
    color: DURATION_COLOR[d],
  }));

  const staffEntries = Object.entries(report.massage.staffs).sort(
    ([, a], [, b]) =>
      (b.items || []).reduce((s, i) => s + (i.price || 0), 0) -
      (a.items || []).reduce((s, i) => s + (i.price || 0), 0)
  );

  const activeCategory = report[activeTab];
  const filteredItems = (activeCategory?.items || []).filter((item) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return item.invoice_id.toLowerCase().includes(q) || (item.service || "").toLowerCase().includes(q);
  });

  function fetchBookingData() {
    const callApiCheckDataService = async () => {
      try {
         const response = await axios.get(`https://database.azumayareport.com/invoice/${branchID}/statistics_by_month/`+selectDateInput,{
            headers: { Authorization: token }
         });
         if(response) {
          setDataRender(response.data.analytics)
         }
      } catch (error) {
        alert("Error when load data")
      }
    }

    callApiCheckDataService();
  }

  // Button
  function changeSelectBranchID(value) {
      // set value on screen view
      setSelectBranch(value);
      // change BranchID
      const areaFindIndex = selectArea === "Select Area" ? area : selectArea;
      const findindex = allBranchInform[areaFindIndex].findIndex(({ name }) => name === value);
      let branchIdSelect = allBranchInform[areaFindIndex][findindex].branchID;
      dispatch(datePickup.changeSelectBranchServiceInvoice({...dataBranchOnState, ID: branchIdSelect}));
  }

  return (
    <div className="srd">
      <div className="srd-container">
        <div className="filter-group d-flex" style={{alignItems:"center"}}>
            <label style={{fontSize:"3rem"}}>Service Invoice Month:</label>
            <div className={(role === 1 || role === 7 || role === 6 || role === 5) ? "" : "op-hidden"}>
                <RCSumDropDownSelectBranch
                    onSelect={changeSelectBranchID}
                    onblur={setSelectBranch}
                    selectValue={selectBranch}
                    listRender={listBranch}
                />
            </div>
            
            <input
                type="month"
                className="nav-link-sub"
                href="#"
                onChange={e => {
                  setSelectDataInput(e.target.value)
                }}
                value={selectDateInput}
            />
            <button className="btn btn-primary" 
              style={{padding:"8px", marginLeft:"8px", width:"145px"}}
              onClick={() => {
                  fetchBookingData('ota');
              }}>Find Data</button>
        </div>

        <div className="srd-header">
          <div>
            <h1 className="srd-title">Revenue report</h1>
            <p className="srd-subtitle">
              {formatNumber(grandQty)} transactions across {Object.keys(categories).length} service groups
            </p>
          </div>
          <div className="srd-total-block">
            <p className="srd-total-label">Total revenue</p>
            <p className="srd-total-value">{formatCurrency(grandTotal)}</p>
          </div>
        </div>

        <div className="srd-cat-grid">
          {Object.entries(categories).map(([key, c]) => (
            <CategoryCard
              key={key}
              label={c.label}
              revenue={c.revenue}
              quantity={c.quantity}
              Icon={c.icon}
              active={key === "massage"}
            />
          ))}
        </div>

        <section className="srd-section">
          <div className="srd-section-head">
            <h2 className="srd-section-title">Massage</h2>
            <span className="srd-section-note">
              {formatNumber(report.massage.totalQuantity)} turns · {report.massage.voucherCount} vouchers ·{" "}
              {formatCurrency(report.massage.totalRevenue)}
            </span>
          </div>
          <div className="srd-card">
            <div className="srd-type-grid">
              {typeChartData.map((t) => (
                <div key={t.duration} className="srd-type-box" style={{ borderColor: t.color }}>
                  <div className="srd-type-label">Massage {t.duration}</div>
                  <div className="srd-type-value">{formatCurrency(t.revenue)}</div>
                  <div className="srd-type-sub">{formatNumber(t.count)} turns</div>
                </div>
              ))}
            </div>
            <div style={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={typeChartData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                  <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => `${v / 1000000}M`}
                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis type="category" dataKey="duration" tick={{ fill: "#334155", fontSize: 12 }} axisLine={false} tickLine={false} width={56} />
                  <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]} barSize={22}>
                    {typeChartData.map((t, i) => (
                      <Cell key={i} fill={t.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="srd-section">
          <div className="srd-section-head">
            <h2 className="srd-section-title">
              <Users size={17} color="#94a3b8" /> Staff Massage
            </h2>
            <span className="srd-section-note">Click a row for the 40 / 70 / 100 min breakdown</span>
          </div>
          <div className="srd-card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="srd-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Staff</th>
                  <th>Turn mix</th>
                  <th className="srd-th-right">Turns</th>
                  <th className="srd-th-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {staffEntries.map(([staffId, record]) => (
                  <StaffRow
                    key={staffId}
                    staffId={staffId}
                    record={record}
                    isOpen={openStaff === staffId}
                    onToggle={() => setOpenStaff(openStaff === staffId ? null : staffId)}
                  />
                ))}
              </tbody>
            </table>
            <div className="srd-legend">
              {DURATIONS.map((d) => (
                <span key={d} className="srd-legend-item">
                  <span className="srd-dot" style={{ backgroundColor: DURATION_COLOR[d] }} />
                  {d} min
                </span>
              ))}
            </div>
          </div>
        </section>

        <section>
          <h2 className="srd-section-title" style={{ marginBottom: 16 }}>Other services</h2>
          <div className="srd-card">
            <div className="srd-tabs">
              {OTHER_CATEGORIES.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`srd-tab${activeTab === key ? " active" : ""}`}
                >
                  <Icon size={14} />
                  {label}
                  <span className="srd-tab-count">({report[key].totalQuantity})</span>
                </button>
              ))}
            </div>

            <div className="srd-search-wrap">
              <span className="srd-search-icon">
                <Search size={15} />
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by invoice ID or service name..."
                className="srd-search-input"
              />
            </div>

            <div className="srd-other-note">
              {activeCategory?.totalQuantity} transactions · {formatCurrency(activeCategory?.totalRevenue)}
            </div>

            <div className="srd-other-table-wrap">
              <table className="srd-other-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Service</th>
                    <th className="srd-th-right">Version</th>
                    <th className="srd-th-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.invoice_id}</td>
                      <td>{item.service || "—"}</td>
                      <td className="srd-th-right">v{item.version}</td>
                      <td className="srd-th-right">{formatCurrency(item.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredItems.length === 0 && <div className="srd-empty">No matching transactions.</div>}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

// Embedded fallback so the component renders something meaningful with no props.
// Replace this with real data via the `data` or `dataUrl` prop in your app.
const SAMPLE_DATA = {"massage":{"types":{"40":{"count":10,"revenue":3150000},"70":{"count":125,"revenue":31970000},"100":{"count":36,"revenue":16900000}},"staffs":{"17":{"40":2,"70":24,"100":4,"totalTurns":30,"items":[{"invoice_id":"0004","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0005","version":2,"service":"Massage 70 min","price":700000},{"invoice_id":"0006","version":1,"service":"Massage 70 min","price":175000},{"invoice_id":"0010","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0023","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0039","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0049","version":1,"service":"Massage 70 min (1 voucher)","price":945000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0052","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0059","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0071","version":3,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0073","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0092","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0098","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0108","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0112","version":1,"service":"Massage 40 min","price":300000},{"invoice_id":"0118","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0121","version":1,"service":"Massage 70 min (2 VOUCHER)","price":315000}]},"20":{"40":1,"70":24,"100":5,"totalTurns":30,"items":[{"invoice_id":"0003","version":1,"service":"Massage 100 min","price":2000000},{"invoice_id":"0007","version":1,"service":"Massage 70 min (1VC)","price":315000},{"invoice_id":"0009","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0022","version":1,"service":"Massage 70 min","price":315000},{"invoice_id":"0031","version":1,"service":"Massage 70 min ( VC )","price":315000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0042","version":2,"service":"Massage 100 min","price":900000},{"invoice_id":"0049","version":1,"service":"Massage 70 min (1 voucher)","price":945000},{"invoice_id":"0049","version":1,"service":"Massage 70 min (1 voucher)","price":945000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0055","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0060","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0071","version":3,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0076","version":1,"service":"Massage 40 min","price":900000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0097","version":1,"service":"Massage 70 min (2VC)","price":315000},{"invoice_id":"0098","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000}]},"23":{"40":2,"70":22,"100":6,"totalTurns":30,"items":[{"invoice_id":"0003","version":1,"service":"Massage 100 min","price":2000000},{"invoice_id":"0005","version":2,"service":"Massage 70 min","price":700000},{"invoice_id":"0009","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0020","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0028","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0042","version":2,"service":"Massage 100 min","price":900000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0051","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0055","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0059","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0071","version":3,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0073","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0076","version":1,"service":"Massage 40 min","price":900000},{"invoice_id":"0089","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0092","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0097","version":1,"service":"Massage 70 min (2VC)","price":315000},{"invoice_id":"0098","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0108","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0117","version":1,"service":"Rotenburo + Massage 70 min","price":1000000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000}]},"27":{"40":1,"70":15,"100":7,"totalTurns":23,"items":[{"invoice_id":"0003","version":1,"service":"Massage 100 min","price":2000000},{"invoice_id":"0003","version":1,"service":"Massage 100 min","price":2000000},{"invoice_id":"0011","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0023","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0028","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0039","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0092","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0093","version":1,"service":"Massage 100 min","price":450000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0108","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0109","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0113","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0117","version":1,"service":"Rotenburo + Massage 70 min","price":1000000},{"invoice_id":"0118","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0121","version":1,"service":"Massage 70 min (2 VOUCHER)","price":315000}]},"31":{"40":3,"70":17,"100":9,"totalTurns":29,"items":[{"invoice_id":"0004","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0005","version":2,"service":"Massage 70 min","price":700000},{"invoice_id":"0011","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0016","version":1,"service":"Massage 70 min ( 1 VC )","price":315000},{"invoice_id":"0027","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0051","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0052","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0057","version":1,"service":"Massage 40 min","price":300000},{"invoice_id":"0060","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0073","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0076","version":1,"service":"Massage 40 min","price":900000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0089","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0099","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0100","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0118","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0118","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000}]},"34":{"40":0,"70":23,"100":5,"totalTurns":28,"items":[{"invoice_id":"0021","version":1,"service":"Massage 100 min","price":450000},{"invoice_id":"0027","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0043","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0049","version":1,"service":"Massage 70 min (1 voucher)","price":945000},{"invoice_id":"0052","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0056","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0092","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0098","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0108","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0114","version":1,"service":"Massage 70 min","price":315000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0121","version":1,"service":"Massage 70 min (2 VOUCHER)","price":315000},{"invoice_id":"0128","version":1,"service":"Massage 70 min","price":350000}]}},"voucherCount":25,"totalQuantity":171,"totalRevenue":52020000,"items":[{"invoice_id":"0003","version":1,"service":"Massage 100 min","price":2000000},{"invoice_id":"0004","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0005","version":2,"service":"Massage 70 min","price":700000},{"invoice_id":"0006","version":1,"service":"Massage 70 min","price":175000},{"invoice_id":"0007","version":1,"service":"Massage 70 min (1VC)","price":315000},{"invoice_id":"0009","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0010","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0011","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0016","version":1,"service":"Massage 70 min ( 1 VC )","price":315000},{"invoice_id":"0020","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0021","version":1,"service":"Massage 100 min","price":450000},{"invoice_id":"0022","version":1,"service":"Massage 70 min","price":315000},{"invoice_id":"0023","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0027","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0028","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0031","version":1,"service":"Massage 70 min ( VC )","price":315000},{"invoice_id":"0036","version":1,"service":"Massage 100 min","price":3500000},{"invoice_id":"0038","version":1,"service":"Massage 70 min","price":2100000},{"invoice_id":"0039","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0040","version":1,"service":"Rotenburo + Massage 40 min","price":450000},{"invoice_id":"0042","version":2,"service":"Massage 100 min","price":900000},{"invoice_id":"0043","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0049","version":1,"service":"Massage 70 min (1 voucher)","price":945000},{"invoice_id":"0050","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0051","version":1,"service":"Massage 100 min","price":900000},{"invoice_id":"0052","version":1,"service":"Massage 100 min","price":1000000},{"invoice_id":"0055","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0056","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0057","version":1,"service":"Massage 40 min","price":300000},{"invoice_id":"0059","version":1,"service":"Massage 70 min","price":700000},{"invoice_id":"0060","version":1,"service":"Massage 70 min","price":630000},{"invoice_id":"0071","version":3,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0073","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0074","version":1,"service":"Massage 100 min","price":2500000},{"invoice_id":"0075","version":2,"service":"Massage 70 min ( 3 VC )","price":1575000},{"invoice_id":"0076","version":1,"service":"Massage 40 min","price":900000},{"invoice_id":"0077","version":1,"service":"Massage 70 min","price":2450000},{"invoice_id":"0089","version":1,"service":"Massage 40 min","price":600000},{"invoice_id":"0090","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0091","version":1,"service":"Massage 70 min (2VC)","price":945000},{"invoice_id":"0092","version":1,"service":"Rotenburo + Massage 70 min","price":1500000},{"invoice_id":"0093","version":1,"service":"Massage 100 min","price":450000},{"invoice_id":"0097","version":1,"service":"Massage 70 min (2VC)","price":315000},{"invoice_id":"0098","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0099","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0100","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0102","version":1,"service":"Massage 70 min (5 VC)","price":315000},{"invoice_id":"0106","version":1,"service":"Massage 70 min","price":1750000},{"invoice_id":"0107","version":1,"service":"Massage 70 min (3VC)","price":630000},{"invoice_id":"0108","version":1,"service":"Rotenburo + Massage 70 min","price":500000},{"invoice_id":"0109","version":1,"service":"Massage 100 min","price":500000},{"invoice_id":"0111","version":1,"service":"Massage 70 min (5VC)","price":315000},{"invoice_id":"0112","version":1,"service":"Massage 40 min","price":300000},{"invoice_id":"0113","version":1,"service":"Massage 70 min","price":350000},{"invoice_id":"0114","version":1,"service":"Massage 70 min","price":315000},{"invoice_id":"0117","version":1,"service":"Rotenburo + Massage 70 min","price":1000000},{"invoice_id":"0118","version":1,"service":"Massage 70 min","price":1050000},{"invoice_id":"0119","version":1,"service":"Massage 70 min (2 VC)","price":1575000},{"invoice_id":"0121","version":1,"service":"Massage 70 min (2 VOUCHER)","price":315000},{"invoice_id":"0128","version":1,"service":"Massage 70 min","price":350000}]},"breakfast":{"totalQuantity":5,"totalRevenue":1860000,"items":[{"invoice_id":"0033","version":1,"service":"BreakFast","price":660000},{"invoice_id":"0069","version":1,"service":"BreakFast","price":220000},{"invoice_id":"0086","version":1,"service":"BreakFast","price":620000},{"invoice_id":"0110","version":1,"service":"BreakFast","price":180000},{"invoice_id":"0130","version":1,"service":"BreakFast","price":180000}]},"rotenburo":{"totalQuantity":17,"totalRevenue":22025000,"items":[{"invoice_id":"0012","version":1,"service":"Rotenburo","price":1000000},{"invoice_id":"0024","version":1,"service":"Rotenburo","price":2750000},{"invoice_id":"0026","version":2,"service":"Rotenburo","price":1500000},{"invoice_id":"0030","version":1,"service":"Rotenburo","price":250000},{"invoice_id":"0035","version":1,"service":"Rotenburo","price":500000},{"invoice_id":"0044","version":1,"service":"Rotenburo","price":4220000},{"invoice_id":"0054","version":1,"service":"Rotenburo","price":250000},{"invoice_id":"0058","version":1,"service":"Rotenburo","price":1000000},{"invoice_id":"0061","version":2,"service":"Rotenburo","price":250000},{"invoice_id":"0078","version":2,"service":"Rotenburo + Mini Bar","price":2030000},{"invoice_id":"0082","version":1,"service":"Rotenburo + Minibar","price":270000},{"invoice_id":"0094","version":1,"service":"Rotenburo + Minibar","price":1735000},{"invoice_id":"0101","version":1,"service":"Rotenburo","price":1250000},{"invoice_id":"0115","version":1,"service":"Rotenburo","price":1750000},{"invoice_id":"0120","version":1,"service":"Rotenburo + Minibar","price":1770000},{"invoice_id":"0122","version":1,"service":"Rotenburo","price":1000000},{"invoice_id":"0127","version":1,"service":"Rotenburo","price":500000}]},"exchange":{"totalQuantity":22,"totalRevenue":1500000,"items":[{"invoice_id":"0002","version":1,"service":"Exchange Money (20.000JPY X 150 = 3.000.000VND)","price":0},{"invoice_id":"0008","version":1,"service":"Exchange Money (5000JPY X 150 = 750.000VND","price":0},{"invoice_id":"0014","version":1,"service":"Exchange Money","price":1500000},{"invoice_id":"0018","version":1,"service":"Exchange Money : 10.000JPY x 150 = 1.500.000 VND","price":0},{"invoice_id":"0019","version":1,"service":"Exchange Money ( 10.000 jpy x 150 = 1.500.000vnd)","price":0},{"invoice_id":"0034","version":1,"service":"Exchange Money(20.000YEN x 150 = 3.000.000VND","price":0},{"invoice_id":"0037","version":1,"service":"Exchange Money 10.000 JPN X 150 = 1.500.000 VND","price":0},{"invoice_id":"0070","version":1,"service":"Exchange Money 10.000jpy x 150 = 1.500.000vnd","price":0},{"invoice_id":"0072","version":1,"service":"Exchange Money 4.000 X 150 = 600.000 VND","price":0},{"invoice_id":"0079","version":1,"service":"Exchange Money (60.000YEN X 150 = 9.000.000)","price":0},{"invoice_id":"0083","version":1,"service":"Exchange Money(10.000YEN X 150 = 1.500.000)","price":0},{"invoice_id":"0084","version":1,"service":"Exchange Money(10.000YEN X 150 = 1.500.000)","price":0},{"invoice_id":"0085","version":1,"service":"Exchange Money(10.000 yen x 150 = 1.500.000)","price":0},{"invoice_id":"0087","version":1,"service":"Exchange Money 20.000YEN X 150 = 3.000.000","price":0},{"invoice_id":"0088","version":1,"service":"Exchange Money (10.000YEN X 150 = 1.500.000)","price":0},{"invoice_id":"0095","version":1,"service":"Exchange Money 10.000 YEN X 150 = 1.500.000","price":0},{"invoice_id":"0096","version":1,"service":"Exchange Money (40.000YEN X 150 = 6.000.000","price":0},{"invoice_id":"0104","version":1,"service":"Exchange Money ( 20.000 YEN X 150 = 3.000.000 VND )","price":0},{"invoice_id":"0105","version":1,"service":"Exchange Money (10.000 YEN X 150 = 1.500.000)","price":0},{"invoice_id":"0116","version":1,"service":"Exchange Money (10.000jpy x 150 = 1.500.000vnd)","price":0},{"invoice_id":"0125","version":1,"service":"Exchange Money(20.000YEN X 150 = 3.000.000","price":0},{"invoice_id":"0126","version":1,"service":"Exchange Money(20.000 YEN X 150 = 3.000.000)","price":0}]},"other":{"totalQuantity":26,"totalRevenue":9006000,"items":[{"invoice_id":"0001","version":0,"service":"","price":0},{"invoice_id":"0013","version":1,"service":"Minibar (PAID AT M0012)","price":30000},{"invoice_id":"0015","version":1,"service":"Laundry","price":100000},{"invoice_id":"0017","version":2,"service":"Minibar","price":40000},{"invoice_id":"0025","version":1,"service":"Free Laundry: 230.000","price":0},{"invoice_id":"0029","version":1,"service":"REFUND OF 16635C","price":211000},{"invoice_id":"0032","version":1,"service":"FREE LAUNDRY: 110,000","price":0},{"invoice_id":"0041","version":1,"service":"27+17+31+34+17+27+23+31+34","price":1260000},{"invoice_id":"0045","version":2,"service":"Minibar( PAID AT M0044)","price":0},{"invoice_id":"0048","version":1,"service":"Laundry","price":960000},{"invoice_id":"0053","version":1,"service":"Minibar","price":70000},{"invoice_id":"0046","version":1,"service":"Laundry","price":220000},{"invoice_id":"0047","version":1,"service":"Laundry","price":570000},{"invoice_id":"0062","version":1,"service":"Free Laundry","price":890000},{"invoice_id":"0063","version":1,"service":"Laundry","price":90000},{"invoice_id":"0064","version":1,"service":"Laundry","price":490000},{"invoice_id":"0065","version":1,"service":"Laundry","price":490000},{"invoice_id":"0066","version":1,"service":"Free Laundry","price":300000},{"invoice_id":"0067","version":1,"service":"Free Laundry","price":2590000},{"invoice_id":"0068","version":1,"service":"Free Laundry","price":60000},{"invoice_id":"0080","version":1,"service":"FREE LAUNDRY: 1.080.000","price":0},{"invoice_id":"0081","version":1,"service":"FREE LAUNDRY: 120.000","price":0},{"invoice_id":"0103","version":1,"service":"Free Laundry","price":240000},{"invoice_id":"0123","version":1,"service":"Free Laundry","price":240000},{"invoice_id":"0124","version":1,"service":"Laundry","price":140000},{"invoice_id":"0129","version":1,"service":"Minibar(staff off 50%)","price":15000}]}};
